import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { motivationColor } from '../../helpers/motivationColors'
import { formatDelta, formatSharePct, NO_VALUE } from '../../helpers/scoreDisplay'
import { deltaColor, findMotivation, patternsForMotivation } from '../../helpers/showcase'
import { useShowcaseMotivationCreatives } from '../../hooks/useShowcaseMotivationCreatives'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { ShowcaseData } from '../../types'
import { MotivationDetailLayout } from '../MotivationDetailLayout/MotivationDetailLayout'

interface MotivationDetailViewProps {
  data: ShowcaseData
  motivationId: string
}

/**
 * In-page Motivation detail view: shows the story patterns and top creatives
 * that lean on a single creative motivation. Drilling into a pattern carries
 * this motivation as the pattern's lens.
 */
export const MotivationDetailView: FC<MotivationDetailViewProps> = ({ data, motivationId }) => {
  const { t } = useTranslation('ua-insights')
  const { motivationLabel } = useUATaxonomy()
  const { motivationById } = useUAInsightsMeta()
  const { openPatternView } = useUAInsightsActions()

  const stats = findMotivation(data.motivations, motivationId)
  const motivation = stats?.motivation ?? motivationById(motivationId)
  const patterns = patternsForMotivation(data.narrativePatterns, data.narrativeMotivations, motivationId)
  const { data: creativesData } = useShowcaseMotivationCreatives({ motivationId })
  const creatives = creativesData?.creatives ?? []

  if (!motivation) return null
  const color = motivationColor(motivation)

  return (
    <MotivationDetailLayout
      title={motivationLabel(motivation)}
      motivation={motivation}
      color={color}
      stats={[
        { value: stats ? formatSharePct(stats.sharePct) : NO_VALUE, label: t('showcase_stat_share_of_creatives'), color },
        { value: formatDelta(stats?.deltaPp), label: t('showcase_stat_vs_last_month'), color: deltaColor(stats?.deltaPp) },
        { value: stats?.creativesCount ?? NO_VALUE, label: t('showcase_stat_creatives') },
      ]}
      patterns={patterns}
      onSelectPattern={(patternId) => openPatternView(patternId, motivationId)}
      creatives={creatives}
      creativesTotal={creativesData?.page.total}
    />
  )
}
